import { Input } from '@agavi/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';

import LeftPanel from '@/components/auth/left-panel';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/api';
import { resetPasswordSchema, type ResetPasswordFormValues } from '@/types/auth';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const resetToken = (location.state as { resetToken?: string } | null)?.resetToken ?? '';

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  // Guard AFTER every hook, never before: an early return above them makes the hook count depend on
  // props/state, and React then throws "Rendered fewer hooks than expected" the moment this renders
  // once with a value and once without. Redirecting a render later costs nothing — the effect below
  // cleans itself up on the unmount the redirect causes.
  if (!resetToken) {
    return <Navigate to='/forgot-password' replace />;
  }

  async function onSubmit(data: ResetPasswordFormValues) {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { resetToken, password: data.password });
      navigate('/password-updated', { state: { confirmed: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='flex min-h-screen bg-ws-bg'>
      <LeftPanel />

      <div className='flex flex-1 items-center justify-center px-6'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full max-w-[320px] space-y-8'
          >
            {/* Header */}
            <div className='text-center space-y-2'>
              <h1 className='text-3xl font-semibold text-ws-text'>
                {t('auth.resetPassword.title')}
              </h1>
              <p className='text-base text-ws-muted'>
                {t('auth.resetPassword.subtitle')}
              </p>
            </div>

            {/* Fields */}
            <div className='space-y-6'>
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base font-medium'>
                      {t('auth.resetPassword.newPassword')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <p className='text-xs text-ws-muted'>
                      {t('auth.resetPassword.newPasswordHint')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-base font-medium'>
                      {t('auth.resetPassword.confirmPassword')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <p className='text-xs text-ws-muted'>
                      {t('auth.resetPassword.confirmPasswordHint')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Error */}
            {error && <p className='text-sm text-ws-error-fg text-center'>{error}</p>}

            {/* Actions */}
            <Button type='submit' variant='primary' size='lg' block busy={loading}>
              {loading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
